import { createStore, filterNil, select, withProps } from '@ngneat/elf';
import { joinRequestResult, trackRequestResult } from '@ngneat/elf-requests';
import { AuthenticatedUser, Jwt } from '@tmx/data-access';
import { shareReplay, Subject } from 'rxjs';

import { defaultAuthConfig } from '../auth.config';
import { AuthConfig, AuthErrorMap, AuthTextMap } from '../models/auth-config';
import { AuthErrorType } from '../models/auth-error-type';
import { AuthState } from '../models/auth-state';
import {
  TID_ACCESS_TOKEN_KEY,
  TID_ID_TOKEN_KEY,
} from '../models/local-storage-keys';

type RequestKeys = 'authenticate-user' | 'fetch-jwt' | 'username-exists';

interface AuthProps {
  authCode: null | string;
  authConfig: AuthConfig;
  authErrorMessage: null | string;
  authState: AuthState;
  authUser: AuthenticatedUser | null;
  jwt: Jwt | null;
  originalAccountId: null | number;
  ssoAccounts: string[];
  tidRedirectUrl: string;
  usernameExists: boolean;
}

const authStore = createStore(
  { name: '@tmx/auth' },
  withProps<AuthProps>({
    authConfig: {
      ...defaultAuthConfig,
      errorMap: { ...defaultAuthConfig.errorMap },
    },
    authState: {
      state: 'idle',
    },
    usernameExists: false,
    authErrorMessage: null,
    authCode: null,
    authUser: null,
    originalAccountId: null,
    ssoAccounts: [],
    jwt: null,
    tidRedirectUrl: '',
  })
);

/** Snapshots */

export const authStoreSnapshot = () => {
  return authStore.getValue();
};

export const authConfigSnapshot = () => {
  const { authConfig } = authStore.getValue();
  return authConfig;
};

export const authCodeSnapshot = () => {
  const { authCode } = authStore.getValue();
  if (authCode === null) {
    throw new Error('authCode not set');
  }
  return authCode;
};

export const authUserSnapshot = () => {
  const { authUser } = authStore.getValue();
  if (authUser === null) {
    throw new Error('authUser not set');
  }
  return authUser;
};

export const jwtSnapshot = () => {
  const { jwt } = authStore.getValue();
  if (jwt === null) {
    throw new Error('jwt not set');
  }
  return jwt;
};

/** Request Results Trackers */

export const trackAuthRequests = <T>(keys: RequestKeys[]) =>
  trackRequestResult<T>(keys, {
    skipCache: true,
    preventConcurrentRequest: false,
  });
export const joinAuthRequests = <T>(keys: RequestKeys[]) =>
  joinRequestResult<T>(keys);

/** Store Observables */

export const authState$ = authStore.pipe(
  select(({ authState }) => authState),
  shareReplay({ bufferSize: 1, refCount: true })
);

export const usernameExists$ = authStore.pipe(
  select(({ usernameExists }) => usernameExists),
  joinAuthRequests(['username-exists'])
);

export const authUser$ = authStore.pipe(
  select(({ authUser }) => authUser),
  joinAuthRequests(['authenticate-user'])
);

export const ssoAccounts$ = authStore.pipe(
  select(({ ssoAccounts }) => ssoAccounts)
);

export const authErrorMessage$ = authStore.pipe(
  select(({ authErrorMessage }) => authErrorMessage)
);

export const jwt$ = authStore.pipe(
  select(({ jwt }) => jwt),
  joinAuthRequests(['fetch-jwt'])
);

export const tidRedirectUrl$ = authStore.pipe(
  select(({ tidRedirectUrl }) => tidRedirectUrl),
  filterNil()
);

/** Store Updaters */

export function updateAuthConfig(authConfig: AuthConfig) {
  authStore.update((state) => ({
    ...state,
    authConfig,
  }));
}

export function updateAuthTextMap(authTextMap: AuthTextMap) {
  const textMap: Partial<AuthTextMap> = {};

  type Keys = keyof AuthTextMap;

  for (const key of Object.keys(authTextMap) as Keys[]) {
    if (authTextMap[key] !== '') {
      textMap[key] = authTextMap[key];
    }
  }

  authStore.update((state) => ({
    ...state,
    authConfig: {
      ...state.authConfig,
      textMap: {
        ...state.authConfig.textMap,
        ...textMap,
      },
    },
  }));
}

export function updateAuthErrorMap(authErrorMap: AuthErrorMap) {
  const errorMap: Partial<Record<AuthErrorType, string>> = {};

  for (const key of Object.keys(authErrorMap) as AuthErrorType[]) {
    if (authErrorMap[key] !== '') {
      errorMap[key] = authErrorMap[key];
    }
  }

  authStore.update((state) => ({
    ...state,
    authConfig: {
      ...state.authConfig,
      errorMap: {
        ...state.authConfig.errorMap,
        ...errorMap,
      },
    },
  }));
}

export function updateAuthState(authState: AuthState) {
  authStore.update((state) => ({
    ...state,
    authState,
  }));
}

export function updateCheckedForExistingSession(
  checkedForExistingSession: boolean
) {
  authStore.update((state) => ({
    ...state,
    checkedForExistingSession,
  }));
}

export function updateUsernameExists(usernameExists: boolean) {
  authStore.update((state) => ({
    ...state,
    usernameExists,
  }));
}

export function updateAuthUser(authUser: AuthenticatedUser) {
  const originalAccountId =
    authStore.getValue().originalAccountId ?? authUser.accountId;

  authUser.idToken ??= localStorage.getItem(TID_ID_TOKEN_KEY(authUser.userId));

  authUser.accessToken ??= localStorage.getItem(
    TID_ACCESS_TOKEN_KEY(authUser.userId)
  );

  authStore.update((state) => ({
    ...state,
    authUser,
    authCode: null,
    originalAccountId,
  }));
}

export function updateAuthErrorMessage(authErrorMessage: null | string) {
  authStore.update((state) => ({
    ...state,
    authErrorMessage,
  }));
}

export function updateAuthCode(authCode: null | string) {
  authStore.update((state) => ({
    ...state,
    authCode,
  }));
}

export function updateSsoAccounts(ssoAccounts: string[]) {
  authStore.update((state) => ({
    ...state,
    ssoAccounts,
  }));
}

export function updateJwt(jwt: Jwt) {
  authStore.update((state) => ({
    ...state,
    jwt,
  }));
}

export function updateTidRedirectUrl(tidRedirectUrl: string) {
  authStore.update((state) => ({
    ...state,
    tidRedirectUrl,
  }));
}

export function resetAuthStoreOnLogout() {
  authStore.update((state) => ({
    ...state,
    authState: { state: 'idle' },
    checkedForExistingSession: false,
    usernameExists: false,
    authErrorMessage: null,
    authCode: null,
    authUser: null,
    originalAccountId: null,
    ssoAccounts: [],
    jwt: null,
  }));
}

export const isOriginalLogin = () => {
  const currentAccountId = authUserSnapshot().accountId;
  const originalAccountId = authStore.getValue().originalAccountId;
  return currentAccountId === originalAccountId;
};
export const onAuthenticatedSubject$ = new Subject<{
  isOriginalLogin: boolean;
  jwt: Jwt;
  user: AuthenticatedUser;
}>();

/**
 * Get the current Auth Data. This function will throw an Error if no User is currently authenticated.
 * @returns The current Auth Data
 */
export const getCurrentAuthData = () => {
  return {
    /**
     * The current Authenticated User
     */
    user: authUserSnapshot(),
    /**
     * The current User's JWT
     */
    jwt: jwtSnapshot(),
    /**
     * The name of the Application in our company database
     */
    trimbleMapsAppName: authConfigSnapshot().trimbleIdentity.trimbleMapsAppName,
    /**
     * Boolean indicating whether the current authenticated User is in their original account.
     * If the user is switched into a different account, this value will be false.
     */
    isOriginalLogin: isOriginalLogin(),
  };
};

/**
 * Get the current Authenticated User. This function will throw an Error if no User is currently authenticated.
 * @returns The current authenticated User
 */
export const getCurrentAuthenticatedUser = () => authUserSnapshot();
